/**
 * One-time pairing: the code from the admin panel (single use, 15 min) + a key created on THIS device
 * (non-extractable). After this the desk signs in by itself; the device is listed in the admin panel.
 */
import { HopexEmblem, cx, normalizeDigits } from '@hopex/ui';
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ApiFailure, api, signInWithDevice } from '../lib/api.ts';
import { createDeviceKey, saveDevice } from '../lib/device.ts';
import { isIos } from '../lib/platform.ts';
import type { Me } from '../lib/types.ts';

const ALPHABET = /[^2-9A-HJ-NP-Z]/g;
const format = (raw: string) => {
  const v = normalizeDigits(raw).toUpperCase().replace(ALPHABET, '').slice(0, 10);
  return v.length > 5 ? `${v.slice(0, 5)}-${v.slice(5)}` : v;
};

export function PairScreen({
  notice,
  onPaired,
}: {
  notice?: string | undefined;
  onPaired: (me: Me) => void;
}) {
  const [code, setCode] = useState(() => format(window.location.hash.slice(1)));
  const [label, setLabel] = useState(isIos() ? 'آیفون من' : 'دستگاه من');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(notice ?? null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', '/');
    input.current?.focus();
  }, []);

  const ready = code.replace('-', '').length === 10;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      const k = await createDeviceKey();
      const { deviceId } = await api.raw<{ deviceId: string }>('POST', '/pair', {
        code,
        publicKey: k.publicKey,
        label: label.trim().slice(0, 40),
      });
      await saveDevice({ deviceId, key: k.key });
      const me = await signInWithDevice();
      if (!me)
        throw new ApiFailure('DESK_DEVICE_INVALID', 'اتصال انجام نشد. دوباره تلاش کنید.', 401);
      onPaired(me);
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : 'این مرورگر از کلید امن پشتیبانی نمی‌کند. از سافاری، کروم یا اج به‌روز استفاده کنید.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="hx-ambient flex min-h-dvh flex-col bg-canvas px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[calc(env(safe-area-inset-top)+32px)] text-fg"
      data-desk-pair
    >
      <div className="dk-enter mx-auto flex w-full max-w-[400px] flex-1 flex-col gap-6">
        <header className="flex flex-col items-center gap-3 text-center">
          <span className="grid size-16 place-items-center rounded-[22px] bg-canvas-deep shadow-float hx-frame">
            <HopexEmblem size={32} decorative />
          </span>
          <h1 className="m-0 text-[22px] font-extrabold leading-9">اتصال این دستگاه</h1>
          <p className="m-0 text-[13.5px] leading-7 text-fg-muted">
            کد اتصال یک‌بارمصرف را از مدیر بگیرید و اینجا وارد کنید. بعد از آن، این دستگاه همیشه
            خودکار وارد میز پشتیبانی می‌شود.
          </p>
        </header>
        <form
          className="hx-card flex flex-col gap-4 rounded-[24px] border border-line bg-surface-1 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="flex flex-col gap-2">
            <span className="text-[13px] font-semibold text-fg-muted">کد اتصال</span>
            <span className="relative">
              <KeyRound
                aria-hidden
                className="pointer-events-none absolute start-4 top-1/2 size-5 -translate-y-1/2 text-fg-subtle"
              />
              <input
                ref={input}
                dir="ltr"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="one-time-code"
                spellCheck={false}
                value={code}
                onChange={(e) => setCode(format(e.target.value))}
                placeholder="XXXXX-XXXXX"
                aria-invalid={!!error}
                className={cx(
                  'hx-num min-h-14 w-full rounded-[16px] border bg-surface-2 ps-12 pe-4 text-center font-mono text-[22px] font-bold tracking-[0.18em] text-fg outline-hidden placeholder:text-fg-subtle placeholder:tracking-[0.12em] focus-visible:border-accent',
                  error ? 'border-negative' : 'border-line-strong',
                )}
                data-pair-code
              />
            </span>
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[13px] font-semibold text-fg-muted">
              نام این دستگاه (در پنل مدیر دیده می‌شود)
            </span>
            <input
              value={label}
              maxLength={40}
              onChange={(e) => setLabel(e.target.value)}
              className="min-h-12 rounded-[14px] border border-line-strong bg-surface-2 px-4 text-[16px] text-fg outline-hidden focus-visible:border-accent"
              data-pair-label
            />
          </label>
          {error && (
            <p
              role="alert"
              className="m-0 rounded-[12px] bg-negative-tint px-3 py-2 text-[12.5px] leading-6 text-negative"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={!ready || busy}
            className="hx-press inline-flex min-h-13 cursor-pointer items-center justify-center gap-2 rounded-full bg-accent px-6 text-[15px] font-extrabold text-on-accent shadow-cta disabled:cursor-not-allowed disabled:opacity-45"
            data-pair-submit
          >
            {busy && <Loader2 aria-hidden className="size-5 animate-spin" />}
            {busy ? 'در حال اتصال امن…' : 'اتصال این دستگاه'}
          </button>
        </form>
        <p className="m-0 flex items-start gap-2 px-1 text-[12px] leading-6 text-fg-subtle">
          <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0 text-positive" />
          کلید ورود فقط روی همین دستگاه ساخته و نگه داشته می‌شود و قابل کپی نیست. مدیر هر زمان
          بخواهد می‌تواند این دستگاه را از پنل مدیریت قطع کند.
        </p>
      </div>
    </main>
  );
}
